import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { MINT_MIST } from '../../constants/brandSurfaces';

const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';
const headingFont = "'Plus Jakarta Sans', sans-serif";

/* — Page shell ——————————————————————— */
export const Panel = styled('section')({
  marginTop: 12,
  marginBottom: 48,
  padding: '36px 40px 40px',
  borderRadius: 24,
  background: MINT_MIST,
  border: '1px solid rgba(26, 26, 46, 0.06)',
  textAlign: 'left',
  '@media (max-width: 900px)': {
    padding: '28px 24px 32px',
  },
  '@media (max-width: 600px)': {
    marginBottom: 32,
    padding: '22px 12px 24px',
    borderRadius: 18,
  },
});

export const Inner = styled('div')({
  maxWidth: 900,
  margin: '0 auto',
});

export const Eyebrow = styled('p')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  margin: '0 0 8px',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: blue,
  '& svg': { fontSize: 18 },
});

export const Heading = styled('h1')({
  fontFamily: headingFont,
  fontSize: '2rem',
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 8px',
  '@media (max-width: 600px)': {
    fontSize: '1.5rem',
  },
});

export const Subheading = styled('p')({
  fontSize: '1rem',
  lineHeight: 1.55,
  color: muted,
  margin: 0,
});

export const RequestSummary = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: '4px 10px',
  marginTop: 16,
  padding: '12px 16px',
  borderRadius: 14,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.08)',
});

export const RequestSummaryLabel = styled('span')({
  fontSize: '0.86rem',
  fontWeight: 700,
  color: muted,
});

export const RequestSummaryText = styled('span')({
  fontSize: '0.98rem',
  lineHeight: 1.5,
  fontWeight: 600,
  color: ink,
});

export const SearchAgainLink = styled('a')({
  marginLeft: 'auto',
  fontSize: '0.9rem',
  fontWeight: 600,
  color: blue,
  textDecorationColor: 'rgba(13, 78, 254, 0.35)',
  textUnderlineOffset: 3,
});

export const RecommendationList = styled('ol')({
  listStyle: 'none',
  margin: '24px 0 0',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

/* — Card ——————————————————————————— */
export const RecommendationCard = styled('li')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '20px 22px',
  borderRadius: 16,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.08)',
  boxShadow: '0 4px 16px rgba(26, 26, 46, 0.05)',
  '@media (max-width: 600px)': {
    padding: '16px 14px',
  },
});

export const CardHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

export const RankBadge = styled('span')({
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  borderRadius: '50%',
  fontFamily: headingFont,
  fontSize: '0.95rem',
  fontWeight: 800,
  color: blue,
  background: 'rgba(1, 235, 178, 0.18)',
});

export const BusinessName = styled('button')({
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: headingFont,
  fontSize: '1.25rem',
  fontWeight: 700,
  color: ink,
  '&:hover, &:focus-visible': {
    color: blue,
    textDecoration: 'underline',
    textUnderlineOffset: 3,
  },
});

/* AI rationale callout under the header */
export const RecommendationReason = styled('p')({
  margin: 0,
  padding: '10px 14px',
  borderRadius: 10,
  borderLeft: `3px solid ${teal}`,
  background: MINT_MIST,
  fontSize: '0.94rem',
  lineHeight: 1.55,
  color: ink,
});

/* — Info chips ——————————————————— */
export const InfoBar = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
});

export const InfoChip = styled('span', {
  shouldForwardProp: (prop) => prop !== '$tone',
})(({ $tone }) => {
  const tones = {
    green: { background: '#d1fae5', color: '#065f46' },
    blue: { background: '#eff6ff', color: blue },
    neutral: { background: '#f3f4f6', color: '#4b5563' },
  };
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: '0.8rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    ...(tones[$tone] || tones.neutral),
    '& svg': { fontSize: 15 },
  };
});

/* — Sub-services ——————————————————— */
export const RelatedServicesLabel = styled('p')({
  margin: '4px 0 0',
  fontSize: '0.9rem',
  fontWeight: 700,
  color: ink,
});

export const SubServiceList = styled('ul')({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const SubServiceCard = styled('li')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '10px 14px',
  borderRadius: 12,
  background: '#f7fafc',
  border: '1px solid rgba(26, 26, 46, 0.06)',
});

export const SubServiceHeader = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 12,
});

export const SubServiceTitle = styled('span')({
  fontSize: '0.98rem',
  fontWeight: 700,
  color: ink,
});

export const PriceText = styled('span')({
  fontSize: '0.95rem',
  fontWeight: 700,
  color: ink,
  whiteSpace: 'nowrap',
});

export const SubServiceTagline = styled('span')({
  fontSize: '0.88rem',
  lineHeight: 1.4,
  color: muted,
});

export const SubServiceDescription = styled('span')({
  marginTop: 2,
  fontSize: '0.88rem',
  lineHeight: 1.5,
  color: muted,
});

/* — Contact + actions ——————————————— */
export const ContactRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px 20px',
  fontSize: '0.92rem',
  color: muted,
  '& > span': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  '& svg': { fontSize: 18, color: blue },
});

export const ActionRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  paddingTop: 14,
  borderTop: '1px solid rgba(26, 26, 46, 0.06)',
  '@media (max-width: 480px)': {
    '& > *': { flex: '1 1 100%' },
  },
});

const ctaBase = {
  textTransform: 'none',
  borderRadius: 10,
  padding: '9px 20px',
  fontSize: '0.95rem',
  fontWeight: 700,
};

export const PrimaryButton = styled(Button)({
  ...ctaBase,
  color: '#ffffff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.3)',
  '&:hover': {
    background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
    filter: 'brightness(1.05)',
  },
});

export const SecondaryButton = styled(Button)({
  ...ctaBase,
  color: ink,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.16)',
  '&:hover': {
    background: '#ffffff',
    borderColor: blue,
    color: blue,
  },
});

/* — Empty state ——————————————————— */
export const EmptyTitle = styled('h1')({
  fontFamily: headingFont,
  fontSize: '1.5rem',
  fontWeight: 700,
  color: ink,
  margin: '0 0 8px',
});

export const EmptyText = styled('p')({
  fontSize: '1rem',
  lineHeight: 1.6,
  color: muted,
  margin: '0 0 18px',
});

export const EmptyActions = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
});
